import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { formatLempiras } from '../../lib/format'
import Layout from '../Layout'

interface Account { id: number; name: string; slug: string }
interface OrderItem { id: number; product_name: string; variant_name?: string; quantity: number; unit_price_cents: number; line_subtotal_cents?: number; line_total_cents: number; discount_cents?: number; serial_number?: string; requires_serial: boolean }
interface Customer { id: number; name: string; email: string }
interface Branch { id: number; name: string }
interface Order {
  id: number; sequential_number: number; state: string; payment_method: string; fulfillment_type: string
  subtotal_cents: number; tax_cents: number; total_cents: number; placed_at: string; paid_at?: string
  notes?: string; guest_name?: string; guest_email?: string; guest_phone?: string; delivery_address?: string
  reference_number?: string
  order_items: OrderItem[]; customer?: Customer; branch?: Branch
  next_states?: string[]
  payment_proof_url?: string | null
}
interface Props {
  account: Account
  order: Order
  permissions?: Partial<{
    can_view_dashboard: boolean
    can_manage_products: boolean
    can_use_pos: boolean
    can_manage_customers: boolean
    can_manage_categories: boolean
    can_manage_account: boolean
    can_manage_branches: boolean
    can_manage_orders: boolean
    can_manage_sales: boolean
    can_view_reports: boolean
    can_manage_users: boolean
  }>
}

const STATE_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  awaiting_payment: 'Esperando pago',
  confirming: 'Pago en verificación',
  paid: 'Pagado',
  ready_for_pickup: 'Listo para recoger',
  shipped: 'Enviado',
  completed: 'Completado',
  cancelled: 'Cancelado',
}

export default function OrdersShow({ account, order, permissions }: Props) {
  const can_manage = permissions?.can_manage_orders
  const [serials, setSerials] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    order.order_items.forEach((i) => {
      if (i.serial_number) {
        const parts = i.serial_number.split(',').map(s => s.trim())
        parts.forEach((s, idx) => {
          init[`${i.id}_${idx}`] = s
        })
      }
    })
    return init
  })
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [proofOpen, setProofOpen] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)

  const needsSerial = order.order_items.some((i) => i.requires_serial)
  const nextStates = order.next_states ?? []

  const totalSerialInputs = order.order_items
    .filter((i) => i.requires_serial)
    .reduce((sum, i) => sum + i.quantity, 0)
  const filledSerials = totalSerialInputs > 0
    ? order.order_items
        .filter((i) => i.requires_serial)
        .reduce((count, i) => {
          for (let idx = 0; idx < i.quantity; idx++) {
            if (serials[`${i.id}_${idx}`]?.trim()) count++
          }
          return count
        }, 0)
    : 0
  const allSerialsFilled = totalSerialInputs > 0 && filledSerials === totalSerialInputs
  const hasSavedSerials = order.order_items.some((i) => i.requires_serial && i.serial_number)

  const isImageProof = (url?: string | null) => {
    if (!url) return false
    return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url) || url.includes('rails/active_storage')
  }

  const handleTransition = (new_state: string) => {
    // Al pasar de confirming → paid, abrir el diálogo del comprobante
    if (order.state === 'confirming' && new_state === 'paid') {
      setProofOpen(true)
      return
    }
    if (!confirm(`¿Cambiar estado a "${STATE_LABELS[new_state] || new_state}"?`)) return
    router.patch(`/${account.slug}/orders/${order.id}/transition`, { new_state })
  }

  const acceptPayment = () => {
    setActionBusy(true)
    router.patch(
      `/${account.slug}/orders/${order.id}/transition`,
      { new_state: 'paid' },
      {
        onFinish: () => {
          setActionBusy(false)
          setProofOpen(false)
        },
      }
    )
  }

  const rejectPayment = () => {
    if (!confirm('¿Rechazar el comprobante? Se eliminará la imagen y el pedido volverá a "Esperando pago".')) return
    setActionBusy(true)
    router.patch(
      `/${account.slug}/orders/${order.id}/reject_payment`,
      {},
      {
        onFinish: () => {
          setActionBusy(false)
          setProofOpen(false)
        },
      }
    )
  }

  const handleSaveSerials = () => {
    if (!allSerialsFilled) return
    setSaving(true)
    router.patch(
      `/${account.slug}/orders/${order.id}/update_serials`,
      { serial_numbers: serials },
      {
        onSuccess: () => {
          setSaving(false)
          setEditing(false)
        },
        onError: () => setSaving(false),
      }
    )
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="max-w-4xl mx-auto">
        <Link href={`/${account.slug}/orders`} className="text-[#55ACEE] hover:underline text-sm mb-4 inline-block">← Volver a pedidos</Link>

        <div className="flex items-start justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#202020]">Pedido #{order.sequential_number}</h1>
            <p className="text-sm text-gray-500">{new Date(order.placed_at).toLocaleString('es-HN')}</p>
            {order.reference_number && (
              <p className="text-xs text-gray-400 font-mono mt-0.5">Ref: {order.reference_number}</p>
            )}
          </div>
          <span className="text-lg font-semibold px-3 py-1.5 rounded bg-gray-100 whitespace-nowrap">
            {STATE_LABELS[order.state] || order.state}
          </span>
        </div>

        {can_manage && nextStates.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Cambiar estado:</p>
            <div className="flex flex-wrap gap-2">
              {nextStates.map((s) => (
                <button key={s} onClick={() => handleTransition(s)}
                  className={`px-3 py-1.5 text-sm rounded border ${
                    s === 'cancelled'
                      ? 'border-red-300 text-red-700 hover:bg-red-50'
                      : s === 'paid'
                        ? 'border-[#55ACEE] text-[#55ACEE] hover:bg-blue-50'
                        : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                  → {STATE_LABELS[s] || s}
                </button>
              ))}
            </div>
            {order.state === 'confirming' && (
              <p className="text-xs text-gray-500 mt-2">
                Para marcar como Pagado se abrirá el comprobante subido por el cliente.
              </p>
            )}
          </div>
        )}

        {order.payment_proof_url && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="font-semibold text-[#202020]">🧾 Comprobante de pago</h3>
              {can_manage && order.state === 'confirming' && (
                <button
                  onClick={() => setProofOpen(true)}
                  className="text-sm text-[#55ACEE] hover:underline"
                >
                  Verificar comprobante
                </button>
              )}
            </div>
            {isImageProof(order.payment_proof_url) ? (
              <a href={order.payment_proof_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={order.payment_proof_url}
                  alt="Comprobante de transferencia"
                  className="max-h-64 rounded border border-gray-200 object-contain bg-gray-50"
                />
              </a>
            ) : (
              <a href={order.payment_proof_url} target="_blank" rel="noopener noreferrer"
                className="text-[#55ACEE] hover:underline text-sm">
                Ver archivo adjunto
              </a>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-[#202020] mb-2">Cliente</h3>
            {order.customer ? (
              <>
                <p>{order.customer.name}</p>
                <p className="text-sm text-gray-500">{order.customer.email}</p>
              </>
            ) : (
              <>
                <p>{order.guest_name}</p>
                <p className="text-sm text-gray-500">{order.guest_email}</p>
                {order.guest_phone && <p className="text-sm text-gray-500">{order.guest_phone}</p>}
              </>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-[#202020] mb-2">Entrega</h3>
            {order.fulfillment_type === 'pickup' ? (
              <p>🏪 Recoger en {order.branch?.name || '-'}</p>
            ) : (
              <>
                <p>🚚 Envío a domicilio</p>
                {order.delivery_address && <p className="text-sm text-gray-500">{order.delivery_address}</p>}
              </>
            )}
            <p className="text-sm text-gray-500 mt-2">Pago: {order.payment_method}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cant.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit.</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {order.order_items.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-3">
                    {i.product_name}
                    {i.variant_name && <p className="text-xs text-gray-500">{i.variant_name}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">{i.quantity}</td>
                  <td className="px-4 py-3 text-right">{formatLempiras(i.unit_price_cents)}</td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {formatLempiras(i.line_total_cents - (i.discount_cents ?? 0))}
                    {(i.discount_cents ?? 0) > 0 && (
                      <p className="text-[10px] text-green-600 font-medium">−{formatLempiras(i.discount_cents!)}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr><td colSpan={3} className="px-4 py-2 text-right text-gray-600">Subtotal</td><td className="px-4 py-2 text-right">{formatLempiras(order.subtotal_cents)}</td></tr>
              <tr><td colSpan={3} className="px-4 py-2 text-right text-gray-600">IVA</td><td className="px-4 py-2 text-right">{formatLempiras(order.tax_cents)}</td></tr>
              <tr><td colSpan={3} className="px-4 py-3 text-right font-bold text-[#202020]">Total</td><td className="px-4 py-3 text-right font-bold text-[#55ACEE] text-lg">{formatLempiras(order.total_cents)}</td></tr>
            </tfoot>
          </table>
        </div>

        {needsSerial && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h3 className="font-semibold text-[#202020] mb-3">🔢 Números de serie</h3>

            {hasSavedSerials && !editing ? (
              <div className="space-y-2">
                {order.order_items
                  .filter((i) => i.requires_serial)
                  .map((item) => (
                    <div key={item.id}>
                      <p className="text-sm text-gray-600">{item.product_name}</p>
                      <p className="text-sm font-mono text-[#202020] bg-gray-50 rounded px-3 py-1.5">
                        {item.serial_number || '—'}
                      </p>
                    </div>
                  ))}
                {can_manage && (
                  <button
                    onClick={() => setEditing(true)}
                    className="mt-3 text-sm text-[#55ACEE] hover:underline"
                  >
                    ✏️ Editar seriales
                  </button>
                )}
              </div>
            ) : can_manage ? (
              <>
                <div className="space-y-3">
                  {order.order_items
                    .filter((i) => i.requires_serial)
                    .map((item) => (
                      <div key={item.id}>
                        <label className="text-sm text-gray-600 block mb-1">
                          {item.product_name}
                          {item.quantity > 1 && (
                            <span className="text-gray-400"> — {item.quantity} unidad(es)</span>
                          )}
                        </label>
                        <div className="space-y-1">
                          {Array.from({ length: item.quantity }).map((_, idx) => (
                            <input
                              key={idx}
                              type="text"
                              placeholder={`Serial #${idx + 1}`}
                              value={serials[`${item.id}_${idx}`] || ''}
                              onChange={(e) =>
                                setSerials((prev) => ({
                                  ...prev,
                                  [`${item.id}_${idx}`]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55ACEE] font-mono"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={handleSaveSerials}
                    disabled={saving || !allSerialsFilled}
                    className="bg-[#55ACEE] text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Guardando...' : 'Guardar seriales'}
                  </button>
                  {hasSavedSerials && (
                    <button
                      onClick={() => setEditing(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancelar
                    </button>
                  )}
                  {!allSerialsFilled && (
                    <span className="text-xs text-gray-400">
                      {filledSerials}/{totalSerialInputs} completados
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-2">
                {order.order_items
                  .filter((i) => i.requires_serial)
                  .map((item) => (
                    <div key={item.id}>
                      <p className="text-sm text-gray-600">{item.product_name}</p>
                      <p className="text-sm text-gray-400 italic">Sin seriales registrados</p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal verificar comprobante */}
      {proofOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h2 className="font-bold text-[#202020]">Verificar comprobante</h2>
              <button
                onClick={() => !actionBusy && setProofOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar"
              >×</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">
                Pedido #{order.sequential_number} · Total {formatLempiras(order.total_cents)}
              </p>
              {order.payment_proof_url ? (
                isImageProof(order.payment_proof_url) ? (
                  <img
                    src={order.payment_proof_url}
                    alt="Comprobante"
                    className="w-full max-h-[50vh] object-contain rounded border bg-gray-50"
                  />
                ) : (
                  <a href={order.payment_proof_url} target="_blank" rel="noopener noreferrer"
                    className="text-[#55ACEE] hover:underline text-sm block">
                    Abrir archivo adjunto
                  </a>
                )
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  Este pedido no tiene comprobante adjunto.
                </p>
              )}
            </div>
            <div className="px-4 py-3 border-t flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={rejectPayment}
                disabled={actionBusy || !order.payment_proof_url}
                className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-red-300 text-red-700 font-medium hover:bg-red-50 disabled:opacity-50"
              >
                No válido
              </button>
              <button
                onClick={acceptPayment}
                disabled={actionBusy}
                className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#55ACEE] text-white font-semibold hover:bg-[#3a8bc4] disabled:opacity-50"
              >
                {actionBusy ? 'Procesando…' : 'Válido'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
